import { createSignal, createMemo, batch } from "solid-js"
import { createStore, reconcile } from "solid-js/store"
import {
  type ScheduledTask,
  type ExecutionRecord,
  type TaskFormState,
  type ExecutionStatus,
} from "./scheduled-tasks-types"
import { MOCK_TASKS, MOCK_HISTORY, generateId } from "./scheduled-tasks-mock"

const WEEKDAY_LABELS = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"]
const STATUS_LABELS: Record<ExecutionStatus, string> = {
  running: "执行中",
  success: "成功",
  failed: "失败",
}

function scheduleLabel(task: ScheduledTask): string {
  switch (task.schedule_type) {
    case "once":
      return `单次 ${task.scheduled_at ?? ""}`
    case "daily":
      return `每天 ${task.schedule_time}`
    case "weekly":
      return `每周${WEEKDAY_LABELS[task.schedule_weekday ?? 1]} ${task.schedule_time}`
    case "monthly":
      return `每月${task.schedule_month_day ?? 1}日 ${task.schedule_time}`
  }
}

export function createScheduledTasksController() {
  const [tasks, setTasks] = createStore<ScheduledTask[]>(MOCK_TASKS.map((t) => ({ ...t })))
  const [history, setHistory] = createStore<ExecutionRecord[]>(MOCK_HISTORY.map((h) => ({ ...h })))
  const [searchQuery, setSearchQuery] = createSignal("")
  const [statusFilter, setStatusFilter] = createSignal<"all" | "active" | "paused" | "completed">("all")
  const [historyFilter, setHistoryFilter] = createSignal<"all" | ExecutionStatus>("all")
  const [historySearch, setHistorySearch] = createSignal("")
  const [showForm, setShowForm] = createSignal(false)
  const [editingTaskId, setEditingTaskId] = createSignal<string | null>(null)
  const [selectedHistoryId, setSelectedHistoryId] = createSignal<string | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = createSignal<string | null>(null)
  const [loading, setLoading] = createSignal(false)
  const [error, setError] = createSignal<string | null>(null)

  const emptyForm: TaskFormState = {
    name: "",
    description: "",
    prompt: "",
    schedule_type: "daily",
    schedule_time: "09:00",
    schedule_weekday: 1,
    schedule_month_day: 1,
    scheduled_at: "",
    agent: "",
    directory: "",
  }
  const [formData, setFormData] = createStore<TaskFormState>({ ...emptyForm })

  // ===== Computed =====
  const filteredTasks = createMemo<ScheduledTask[]>(() => {
    const q = searchQuery().toLowerCase().trim()
    const sf = statusFilter()
    return tasks.filter((t) => {
      // "paused" 在数量统计里等价于「非运行中」，筛选口径保持一致，避免计数和列表对不上
      if (sf === "active" && t.status !== "active") return false
      if (sf === "paused" && t.status === "active") return false
      if (q && !t.name.toLowerCase().includes(q) && !t.prompt.toLowerCase().includes(q)) return false
      return true
    })
  })

  const filteredHistory = createMemo<ExecutionRecord[]>(() => {
    const q = historySearch().toLowerCase().trim()
    const sf = historyFilter()
    return history.filter((r) => {
      if (sf !== "all" && r.status !== sf) return false
      if (q) {
        const task = tasks.find((t) => t.id === r.task_id)
        const taskName = task?.name ?? ""
        if (!taskName.toLowerCase().includes(q)) return false
      }
      return true
    })
  })

  const selectedHistory = createMemo<ExecutionRecord | null>(() => {
    const id = selectedHistoryId()
    if (!id) return null
    return history.find((r) => r.id === id) ?? null
  })

  // ===== Form actions =====
  function openCreate() {
    batch(() => {
      setFormData({ ...emptyForm })
      setEditingTaskId(null)
      setShowForm(true)
    })
  }

  function openEdit(task: ScheduledTask) {
    batch(() => {
      setFormData({
        name: task.name,
        description: task.description ?? "",
        prompt: task.prompt,
        schedule_type: task.schedule_type,
        schedule_time: task.schedule_time,
        schedule_weekday: task.schedule_weekday ?? 1,
        schedule_month_day: task.schedule_month_day ?? 1,
        scheduled_at: task.scheduled_at ?? "",
        agent: task.agent ?? "",
        directory: task.directory,
      })
      setEditingTaskId(task.id)
      setShowForm(true)
    })
  }

  function closeForm() {
    setShowForm(false)
    setEditingTaskId(null)
  }

  function saveForm() {
    const name = formData.name.trim()
    if (!name) return
    const prompt = formData.prompt.trim()
    if (!prompt) return

    const ts = Date.now()
    const eid = editingTaskId()

    if (eid) {
      setTasks((t) => t.id === eid, (t) => ({
        ...t,
        name,
        description: formData.description || undefined,
        prompt,
        schedule_type: formData.schedule_type,
        schedule_time: formData.schedule_time,
        schedule_weekday: formData.schedule_weekday,
        schedule_month_day: formData.schedule_month_day,
        scheduled_at: formData.scheduled_at || undefined,
        agent: formData.agent || undefined,
        directory: formData.directory || "/tmp",
        time_updated: ts,
      }))
    } else {
      const newTask: ScheduledTask = {
        id: generateId("task"),
        name,
        description: formData.description || undefined,
        prompt,
        schedule_type: formData.schedule_type,
        schedule_time: formData.schedule_time,
        schedule_weekday: formData.schedule_weekday,
        schedule_month_day: formData.schedule_month_day,
        scheduled_at: formData.scheduled_at || undefined,
        status: "active",
        agent: formData.agent || undefined,
        directory: formData.directory || "/tmp",
        next_run_at: undefined,
        last_run_at: undefined,
        time_created: ts,
        time_updated: ts,
      }
      setTasks(reconcile([...tasks, newTask], { key: "id", merge: true }))
    }
    closeForm()
  }

  function toggleStatus(task: ScheduledTask) {
    const newStatus = task.status === "active" ? "paused" : "active"
    setTasks((t) => t.id === task.id, "status", newStatus)
  }

  function deleteTask(id: string) {
    setTasks(reconcile(tasks.filter((t) => t.id !== id), { key: "id", merge: true }))
    setHistory(reconcile(history.filter((h) => h.task_id !== id), { key: "id", merge: true }))
  }

  function runNow(task: ScheduledTask) {
    const execId = generateId("exec")
    const started = Date.now()
    const newRecord: ExecutionRecord = {
      id: execId,
      task_id: task.id,
      status: "success",
      trigger_type: "manual",
      time_started: started,
      time_finished: started + 3000,
    }
    setHistory(reconcile([newRecord, ...history], { key: "id", merge: true }))
    setTasks((t) => t.id === task.id, (t) => ({ ...t, last_run_at: started }))
  }

  // ===== Formatters =====
  function formatTime(ts?: number): string {
    if (!ts) return "—"
    return new Date(ts).toLocaleString("zh-CN", {
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  function formatDuration(ms?: number): string {
    if (!ms) return "—"
    if (ms < 1000) return `${ms}ms`
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
    return `${Math.floor(ms / 60000)}m${Math.floor((ms % 60000) / 1000)}s`
  }

  function getTaskName(taskId: string): string {
    return tasks.find((t) => t.id === taskId)?.name ?? "未知任务"
  }

  return {
    tasks: filteredTasks,
    allTasks: () => tasks,
    history: filteredHistory,
    allHistory: () => history,
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    historySearch,
    setHistorySearch,
    historyFilter,
    setHistoryFilter,
    showForm,
    editingTaskId,
    formData,
    setFormData,
    confirmDeleteId,
    setConfirmDeleteId,
    selectedHistoryId,
    setSelectedHistoryId,
    selectedHistory,
    loading,
    error,
    openCreate,
    openEdit,
    closeForm,
    saveForm,
    toggleStatus,
    deleteTask,
    runNow,
    formatTime,
    formatDuration,
    scheduleLabel,
    statusLabels: STATUS_LABELS,
    weekdayLabels: WEEKDAY_LABELS,
    getTaskName,
  }
}

export type ScheduledTasksController = ReturnType<typeof createScheduledTasksController>
